/**
 * Where each piece stands, for a chosen pose.
 *
 * The pose is split across two repos on purpose, along the line that actually
 * matters: the worker owns what the model must PAINT (a hand, a person, props)
 * and this file owns what the studio must BUILD. Neither half is guessable
 * from the other — a hand is not geometry and a 3×3 grid is not a sentence —
 * so they are two descriptions of one idea rather than one description
 * duplicated.
 *
 * Anything that repeats the material is built here, never described. A
 * generator asked for "nine of these" returns nine variations of it; nine real
 * slabs carrying the vendor's own plank images return the vendor's product.
 */

export type PoseLayout = 'flat' | 'lean' | 'stack' | 'grid' | 'fan' | 'scatter' | 'present' | 'panel' | 'joint';

export interface PoseGeometry {
    /** How many real pieces the studio builds. */
    pieces: number;
    layout: PoseLayout;
}

/** Keep in step with the worker's `surface-poses.ts` — same ids, same counts. */
export const POSE_GEOMETRY: Record<string, PoseGeometry> = {
    single: { pieces: 1, layout: 'flat' },
    leaning: { pieces: 1, layout: 'lean' },
    'in-hand': { pieces: 1, layout: 'present' },
    'held-up': { pieces: 1, layout: 'present' },
    'person-holding': { pieces: 1, layout: 'present' },
    stack: { pieces: 5, layout: 'stack' },
    grid: { pieces: 9, layout: 'grid' },
    fan: { pieces: 4, layout: 'fan' },
    'flat-lay': { pieces: 4, layout: 'scatter' },
    // A sample board: tiles standing as a small wall, as a showroom shows them.
    panel: { pieces: 15, layout: 'panel' },
    // Four tiles laid edge to edge with a real joint — the macro of where they meet.
    joint: { pieces: 4, layout: 'joint' },
};

export function poseGeometry(id: string | undefined | null): PoseGeometry {
    return POSE_GEOMETRY[(id || '').toString().trim()] || POSE_GEOMETRY.single;
}

export interface PiecePlacement {
    pos: [number, number, number];
    rot: [number, number, number];
}

/**
 * Deterministic jitter.
 *
 * A scatter that reshuffles on every render is a scatter nobody can frame, and
 * `Math.random` in a component does exactly that. Indexed sine gives an
 * arrangement that looks casual and comes back identical.
 */
const jitter = (i: number, seed: number) => Math.sin((i + 1) * seed) ;

/** A face-up slab turned to face the camera. */
const UPRIGHT = -Math.PI / 2;

/**
 * Where a held piece hangs, in metres.
 *
 * Must stay near the studio's OrbitControls target (0.4 m) — that is what the
 * default camera looks at, so it is the one height that needs no re-framing.
 */
export const PRESENT_HEIGHT = 0.45;

export function placePiece(
    layout: PoseLayout,
    i: number,
    count: number,
    tileW: number,
    tileH: number,
    thickness: number,
): PiecePlacement {
    const gap = Math.max(tileW, tileH) * 0.12;
    const y = thickness / 2;

    switch (layout) {
        case 'grid': {
            // Tight and square — the point is how the pattern repeats, so the
            // gaps read as joints rather than as spacing.
            const cols = Math.ceil(Math.sqrt(count));
            const tight = Math.max(tileW, tileH) * 0.04;
            const c = i % cols;
            const r = Math.floor(i / cols);
            const rows = Math.ceil(count / cols);
            return {
                pos: [
                    (c - (cols - 1) / 2) * (tileW + tight),
                    y,
                    (r - (rows - 1) / 2) * (tileH + tight),
                ],
                rot: [0, 0, 0],
            };
        }

        case 'stack': {
            // Each piece a layer up, nudged off the one below so the stack's
            // side profile — and the material's real thickness — reads.
            const off = Math.max(tileW, tileH) * 0.05;
            return {
                pos: [jitter(i, 2.1) * off, thickness / 2 + i * thickness, jitter(i, 3.7) * off],
                rot: [0, jitter(i, 1.3) * 0.06, 0],
            };
        }

        case 'fan': {
            // Dealt cards: each turned a little further and slid along, so
            // several colourways are visible at once.
            const step = tileW * 0.38;
            return {
                pos: [(i - (count - 1) / 2) * step, thickness / 2 + i * thickness * 0.6, 0],
                rot: [0, (i - (count - 1) / 2) * 0.22, 0],
            };
        }

        case 'scatter': {
            // Loose enough to look arranged by hand, never overlapping — a
            // flat-lay's props go in the gaps.
            const spread = Math.max(tileW, tileH) * 1.15;
            const cols = Math.ceil(Math.sqrt(count));
            const c = i % cols;
            const r = Math.floor(i / cols);
            const rows = Math.ceil(count / cols);
            return {
                pos: [
                    (c - (cols - 1) / 2) * spread + jitter(i, 2.6) * gap,
                    y,
                    (r - (rows - 1) / 2) * spread + jitter(i, 4.2) * gap,
                ],
                rot: [0, jitter(i, 1.9) * 0.18, 0],
            };
        }

        case 'panel': {
            // Standing upright as a board, 3 across, joints tight like a wall
            // laid by a tiler — the pattern repeating across real pieces.
            const cols = 3;
            const rows = Math.ceil(count / cols);
            const joint = 0.002;
            const c = i % cols;
            const r = Math.floor(i / cols);
            return {
                pos: [(c - (cols - 1) / 2) * (tileW + joint), tileH / 2 + (rows - 1 - r) * (tileH + joint), 0],
                rot: [UPRIGHT, 0, 0],
            };
        }

        case 'joint': {
            // 2×2 laid flat with a real 2 mm joint, so a macro of the meeting
            // point shows four edges and the print running up to them.
            const joint = 0.002;
            const c = i % 2;
            const r = Math.floor(i / 2);
            return {
                pos: [(c - 0.5) * (tileW + joint), y, (r - 0.5) * (tileH + joint)],
                rot: [0, 0, 0],
            };
        }

        case 'lean':
            // Standing on its bottom edge, tipped back against whatever the
            // scene puts behind it.
            return { pos: [0, tileH / 2, 0], rot: [UPRIGHT + 0.16, 0, 0] };

        case 'present':
            // Held up: off the ground, tilted towards the camera. The hand or
            // the person is painted around this by the worker — what the studio
            // contributes is the height and the tilt, which is what makes a
            // painted hand land in a believable place.
            //
            // The height is a CONSTANT, not a multiple of the piece. Scaled to
            // the piece it was 0.66 m for a 60x60 and over 1.3 m for a plank,
            // while the studio's orbit control targets 0.4 m — so choosing a
            // holding pose flung the piece above the frame, and whatever got
            // captured afterwards bore no relation to the pose. It sits just
            // above the camera's target, where a flat piece sits, so the shot
            // the vendor already had stays the shot they get.
            return { pos: [0, PRESENT_HEIGHT, 0], rot: [UPRIGHT + 0.3, 0, 0] };

        case 'flat':
        default: {
            const cols = Math.ceil(Math.sqrt(count));
            const c = i % cols;
            const r = Math.floor(i / cols);
            const rows = Math.ceil(count / cols);
            return {
                pos: [(c - (cols - 1) / 2) * (tileW + gap), y, (r - (rows - 1) / 2) * (tileH + gap)],
                rot: [0, 0, 0],
            };
        }
    }
}

/** True when a pose lifts the piece off the ground — no contact shadow below. */
export function isAirborne(layout: PoseLayout): boolean {
    return layout === 'present';
}
